import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { LoaderCircle } from 'lucide-react'

import styles from './SharedDictionaryTable.module.scss'

import type { AdminSharedDictionaryRow } from '@/api/generated'
import DataTable from '@/components/DataTable'
import Input from '@/components/Input'
import Pagination from '@/components/Pagination'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useSharedDictionary } from '@/hooks/useSharedDictionary'
import { EM_DASH, formatIsoDate, scopeLabel } from '@/pages/app/DictionarySnippets/format'

const PAGE_SIZE = 25

const SEARCH_DEBOUNCE_MS = 300

// Mirrors the server's `Query(max_length=200)`; a longer term is a 422, not a narrower result.
const SEARCH_MAX_LENGTH = 200

interface SharedDictionaryTableProps {
  /** Dictionary words and snippets are one table server-side, split by the endpoint's filter. */
  isSnippet: boolean
  replacementHeader: string
  emptyMessage: string
}

// Sorting is off on every column: the server orders and pages the rows, and DataTable only sorts
// the rows it is handed — so a sortable header would reorder the current page and nothing else.
const buildColumns = (replacementHeader: string): ColumnDef<AdminSharedDictionaryRow, string>[] => [
  {
    id: 'word',
    header: 'Trigger',
    accessorFn: (item) => item.word,
    enableSorting: false,
  },
  {
    id: 'replacement',
    header: replacementHeader,
    accessorFn: (item) => item.replacement || EM_DASH,
    enableSorting: false,
    cell: ({ getValue }) => <span className={styles.replacement}>{getValue()}</span>,
  },
  {
    id: 'scope',
    header: 'Shared with',
    accessorFn: scopeLabel,
    enableSorting: false,
  },
  {
    id: 'created',
    header: 'Added',
    accessorFn: (item) => formatIsoDate(item.created_at),
    enableSorting: false,
  },
]

const SharedDictionaryTable = ({ isSnippet, replacementHeader, emptyMessage }: SharedDictionaryTableProps) => {
  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  // A narrowed result set must not open on a page past the end of it, and resetting in the same
  // tick as the term keeps it to one request rather than one against the page being left.
  const search = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS, () => setPage(0))

  const { data, isLoading, isFetching, isError } = useSharedDictionary({
    isSnippet,
    search,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    // The library is what is live on people's machines; pending belongs to the Pending tab.
    status: 'approved',
  })

  const columns = useMemo(() => buildColumns(replacementHeader), [replacementHeader])
  const total = data?.total ?? 0
  // True while the previous page is still on screen under `keepPreviousData`, or while a typed
  // term has not settled — either way the rows and the pager summary disagree.
  const isStale = isFetching || searchInput.trim() !== search

  const searchBox = (
    <div className={styles.searchRow}>
      <Input
        type="text"
        value={searchInput}
        onInput={setSearchInput}
        // Paging is server-side, so an entry the admin remembers may be several pages away.
        placeholder={isSnippet ? 'Search snippets' : 'Search words'}
        ariaLabel={isSnippet ? 'Search shared snippets' : 'Search shared dictionary words'}
        maxLength={SEARCH_MAX_LENGTH}
        className={styles.search}
      />
      {isStale && <LoaderCircle className={styles.spinner} aria-hidden="true" />}
    </div>
  )

  return (
    <div className={styles.content}>
      {searchBox}
      {isError ? (
        <p className={styles.message}>Could not load shared entries. Refresh to try again.</p>
      ) : (
        <>
          <DataTable
            data={data?.rows ?? []}
            columns={columns}
            isLoading={isLoading}
            manualPagination
            emptyMessage={search ? 'No entries match your search.' : emptyMessage}
            ariaLabel={isSnippet ? 'Shared snippets' : 'Shared dictionary words'}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPrev={() => setPage((current) => Math.max(0, current - 1))}
            onNext={() => setPage((current) => current + 1)}
          />
        </>
      )}
    </div>
  )
}

export default SharedDictionaryTable
